/**
 * Super-admin full control for one pump: edit subscription, custom pricing,
 * invoices/receipts, discounts, and audit trail.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import swal from 'sweetalert';
import {
  addInvoiceReceipt,
  adminAdjustPumpInvoice,
  adminUpdateSubscription,
  approvePumpDraftInvoice,
  deleteInvoiceReceipt,
  fetchAdminPlans,
  fetchInvoiceReceipts,
  fetchPumpSubscriptionControl,
  updateInvoiceReceipt,
} from '../adminApi';
import { formatCurrency } from '../../Manager/SubscriptionManagement/subscriptionApi';
import { formatInrAmount } from '../../../../utils/reportFormat';

const TABS = [
  { id: 'subscription', label: 'Subscription' },
  { id: 'invoices', label: 'Invoices & receipts' },
  { id: 'audit', label: 'Audit trail' },
];

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'online', label: 'Online' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'credit', label: 'Credit' },
];

function emptySubForm() {
  return {
    plan_id: '',
    subscription_start_date: '',
    subscription_end_date: '',
    amount: '',
    discount_amount: '',
    payment_status: '1',
    note: '',
    reason: '',
    sync_invoice: true,
  };
}

function formFromSubscription(sub) {
  if (!sub) return emptySubForm();
  return {
    plan_id: String(sub.plan_id || ''),
    subscription_start_date: sub.subscription_start_date || '',
    subscription_end_date: sub.subscription_end_date || '',
    amount: sub.amount != null ? String(sub.amount) : '',
    discount_amount: sub.discount_amount != null ? String(sub.discount_amount) : '0',
    payment_status: String(sub.payment_status ?? 1),
    note: '',
    reason: '',
    sync_invoice: true,
  };
}

const AdminSubscriptionControlModal = ({ pumpId, plans: plansProp = [], onClose, onSaved }) => {
  const [tab, setTab] = useState('subscription');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [plans, setPlans] = useState(plansProp);
  const [selectedSubId, setSelectedSubId] = useState(null);
  const [subForm, setSubForm] = useState(emptySubForm);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [invoiceForm, setInvoiceForm] = useState({ discount: '', note: '', reason: '' });
  const [receiptForm, setReceiptForm] = useState({
    amount: '',
    payment_mode: 'cash',
    reference_no: '',
    paid_by: '',
    receipt_date: new Date().toISOString().slice(0, 10),
    note: '',
  });
  const [editingReceiptId, setEditingReceiptId] = useState(null);

  const selectedSub = useMemo(
    () => (detail?.subscriptions || []).find((s) => String(s.id) === String(selectedSubId)) || null,
    [detail, selectedSubId]
  );

  const selectedInvoice = useMemo(
    () => (detail?.invoices || []).find((inv) => String(inv.id) === String(selectedInvoiceId)) || null,
    [detail, selectedInvoiceId]
  );

  const load = useCallback(async () => {
    if (!pumpId) return;
    setLoading(true);
    try {
      const [controlRes, planRes] = await Promise.all([
        fetchPumpSubscriptionControl(pumpId),
        plansProp.length ? Promise.resolve({ plans: plansProp }) : fetchAdminPlans(),
      ]);
      if (!controlRes.status) {
        throw new Error(controlRes.message || 'Unable to load subscription control');
      }
      setDetail(controlRes);
      setPlans(planRes.plans || plansProp || []);
      const currentId = controlRes.current?.id || controlRes.subscriptions?.[0]?.id || null;
      setSelectedSubId(currentId);
      setSubForm(formFromSubscription(controlRes.current || controlRes.subscriptions?.[0]));
      const firstInvoice = (controlRes.invoices || [])[0];
      setSelectedInvoiceId(firstInvoice?.id || null);
      if (firstInvoice) {
        setInvoiceForm({
          discount: String(firstInvoice.discount ?? 0),
          note: firstInvoice.note || '',
          reason: '',
        });
      }
    } catch (error) {
      swal('Error', error.message, 'error');
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [pumpId, plansProp, onClose]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedInvoiceId || !pumpId) {
      setReceipts([]);
      return undefined;
    }
    let cancelled = false;
    fetchInvoiceReceipts(pumpId, selectedInvoiceId)
      .then((res) => {
        if (!cancelled) setReceipts(res.receipts || []);
      })
      .catch(() => {
        if (!cancelled) setReceipts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [pumpId, selectedInvoiceId]);

  function selectSubscription(subId) {
    const sub = (detail?.subscriptions || []).find((s) => String(s.id) === String(subId));
    setSelectedSubId(subId);
    setSubForm(formFromSubscription(sub));
  }

  function selectInvoice(invoice) {
    setSelectedInvoiceId(invoice.id);
    setEditingReceiptId(null);
    setInvoiceForm({
      discount: String(invoice.discount ?? 0),
      note: invoice.note || '',
      reason: '',
    });
    setReceiptForm((prev) => ({
      ...prev,
      amount: invoice.amount_pending > 0 ? String(invoice.amount_pending) : '',
    }));
  }

  async function handleSaveSubscription() {
    if (!selectedSubId) {
      swal('No subscription', 'Select or add a subscription first', 'warning');
      return;
    }
    setSaving(true);
    try {
      const res = await adminUpdateSubscription(selectedSubId, {
        plan_id: subForm.plan_id || undefined,
        subscription_start_date: subForm.subscription_start_date || undefined,
        subscription_end_date: subForm.subscription_end_date || undefined,
        amount: subForm.amount,
        discount_amount: subForm.discount_amount,
        payment_status: Number(subForm.payment_status),
        note: subForm.note || undefined,
        reason: subForm.reason || undefined,
        sync_invoice: Boolean(subForm.sync_invoice),
      });
      swal('Saved', res.message || 'Subscription updated', 'success');
      await load();
      onSaved?.();
    } catch (error) {
      swal('Error', error.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveInvoice() {
    if (!selectedInvoice) return;
    setSaving(true);
    try {
      const lineItems = selectedInvoice.line_items || selectedInvoice.draft_line_items || [];
      const res = await adminAdjustPumpInvoice(pumpId, selectedInvoice.id, {
        discount: Number(invoiceForm.discount || 0),
        note: invoiceForm.note,
        reason: invoiceForm.reason || undefined,
        line_items: lineItems,
        op_date: selectedInvoice.op_date,
      });
      swal('Saved', res.message || 'Invoice updated', 'success');
      await load();
      onSaved?.();
    } catch (error) {
      swal('Error', error.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleApproveInvoice() {
    if (!selectedInvoice?.is_draft) return;
    try {
      await approvePumpDraftInvoice(pumpId, selectedInvoice.id);
      swal('Approved', 'Invoice approved', 'success');
      await load();
      onSaved?.();
    } catch (error) {
      swal('Error', error.message, 'error');
    }
  }

  async function handleSaveReceipt() {
    if (!selectedInvoice) return;
    const amount = Number(receiptForm.amount);
    if (!amount || amount <= 0) {
      swal('Amount required', 'Enter a receipt amount', 'warning');
      return;
    }
    setSaving(true);
    try {
      if (editingReceiptId) {
        await updateInvoiceReceipt(pumpId, selectedInvoice.id, editingReceiptId, {
          ...receiptForm,
          amount,
        });
      } else {
        if (selectedInvoice.is_draft) {
          await approvePumpDraftInvoice(pumpId, selectedInvoice.id);
        }
        await addInvoiceReceipt(pumpId, selectedInvoice.id, {
          ...receiptForm,
          amount,
        });
      }
      swal('Saved', editingReceiptId ? 'Receipt updated' : 'Receipt recorded', 'success');
      setEditingReceiptId(null);
      setReceiptForm((prev) => ({ ...prev, amount: '', reference_no: '', note: '' }));
      await load();
      onSaved?.();
    } catch (error) {
      swal('Error', error.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteReceipt(receiptId) {
    const ok = await swal({
      title: 'Delete receipt?',
      text: 'This will recalc invoice pending balance.',
      icon: 'warning',
      buttons: true,
      dangerMode: true,
    });
    if (!ok || !selectedInvoice) return;
    try {
      await deleteInvoiceReceipt(pumpId, selectedInvoice.id, receiptId);
      swal('Removed', 'Receipt deleted', 'success');
      await load();
      onSaved?.();
    } catch (error) {
      swal('Error', error.message, 'error');
    }
  }

  function startEditReceipt(receipt) {
    setEditingReceiptId(receipt.id);
    setReceiptForm({
      amount: String(receipt.amount ?? ''),
      payment_mode: receipt.payment_mode || 'cash',
      reference_no: receipt.reference_no || '',
      paid_by: receipt.paid_by || '',
      receipt_date: receipt.receipt_date || new Date().toISOString().slice(0, 10),
      note: receipt.note || '',
    });
  }

  return (
    <>
      <div className="admin-sub-modal-backdrop" onClick={onClose} />
      <div className="admin-sub-modal admin-sub-control-modal" role="dialog" aria-modal="true">
        <div className="d-flex justify-content-between align-items-start mb-3 gap-2">
          <div>
            <h5 className="mb-0">Subscription control</h5>
            <p className="small text-muted mb-0">
              {detail?.pump?.pump_name || 'Pump'}
              {detail?.pump?.pump_email ? ` · ${detail.pump.pump_email}` : ''}
            </p>
          </div>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
        </div>

        <div className="admin-sub-control-tabs mb-3">
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`admin-sub-control-tab ${tab === item.id ? 'active' : ''}`}
              onClick={() => setTab(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-muted">Loading pump subscription details…</p>
        ) : (
          <>
            {tab === 'subscription' && (
              <div>
                <div className="row g-2 mb-3">
                  <div className="col-md-8">
                    <label className="form-label small">Select subscription period</label>
                    <select
                      className="form-select"
                      value={selectedSubId || ''}
                      onChange={(e) => selectSubscription(e.target.value)}
                    >
                      {(detail?.subscriptions || []).length === 0 && (
                        <option value="">No subscriptions yet</option>
                      )}
                      {(detail?.subscriptions || []).map((sub) => (
                        <option key={sub.id} value={sub.id}>
                          #{sub.id} · {sub.plan_name} · {sub.subscription_start_date} → {sub.subscription_end_date} · {sub.status_label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small">Access</label>
                    <div className="small border rounded p-2 bg-light">
                      {detail?.access?.blocked ? 'Blocked / needs plan' : 'Access open'}
                      {detail?.access?.has_active_subscription ? ' · Active plan' : ''}
                    </div>
                  </div>
                </div>

                {!selectedSub ? (
                  <p className="text-muted">No subscription selected. Use “Add subscription” on the list to create one.</p>
                ) : (
                  <>
                    <div className="admin-sub-billing-summary mb-3">
                      <div><strong>Current plan:</strong> {selectedSub.plan_name}</div>
                      <div>
                        <strong>Catalog price:</strong>{' '}
                        {selectedSub.plan_amount != null ? formatCurrency(selectedSub.plan_amount) : '—'}
                      </div>
                      <div>
                        <strong>Billed:</strong> {formatCurrency(selectedSub.amount)}
                        {Number(selectedSub.discount_amount) > 0
                          ? ` · Discount ${formatCurrency(selectedSub.discount_amount)}`
                          : ''}
                      </div>
                    </div>

                    <div className="row g-2">
                      <div className="col-md-6">
                        <label className="form-label small">Plan</label>
                        <select
                          className="form-select"
                          value={subForm.plan_id}
                          onChange={(e) => setSubForm({ ...subForm, plan_id: e.target.value })}
                        >
                          {plans.map((plan) => (
                            <option key={plan.id} value={plan.id}>
                              {plan.plan_name} · {formatCurrency(plan.amount)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small">Start date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={subForm.subscription_start_date}
                          onChange={(e) => setSubForm({ ...subForm, subscription_start_date: e.target.value })}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small">End date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={subForm.subscription_end_date}
                          onChange={(e) => setSubForm({ ...subForm, subscription_end_date: e.target.value })}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small">Custom amount (₹)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="form-control"
                          value={subForm.amount}
                          onChange={(e) => setSubForm({ ...subForm, amount: e.target.value })}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small">Discount (₹)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="form-control"
                          value={subForm.discount_amount}
                          onChange={(e) => setSubForm({ ...subForm, discount_amount: e.target.value })}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small">Payment status</label>
                        <select
                          className="form-select"
                          value={subForm.payment_status}
                          onChange={(e) => setSubForm({ ...subForm, payment_status: e.target.value })}
                        >
                          <option value="1">Paid / active</option>
                          <option value="0">Unpaid / pending</option>
                        </select>
                      </div>
                      <div className="col-md-3 d-flex align-items-end">
                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="sync-invoice"
                            checked={Boolean(subForm.sync_invoice)}
                            onChange={(e) => setSubForm({ ...subForm, sync_invoice: e.target.checked })}
                          />
                          <label className="form-check-label small" htmlFor="sync-invoice">
                            Sync linked invoice
                          </label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">Internal note (optional)</label>
                        <input
                          type="text"
                          className="form-control"
                          value={subForm.note}
                          onChange={(e) => setSubForm({ ...subForm, note: e.target.value })}
                          placeholder="Stored on subscription reference"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">Audit reason</label>
                        <input
                          type="text"
                          className="form-control"
                          value={subForm.reason}
                          onChange={(e) => setSubForm({ ...subForm, reason: e.target.value })}
                          placeholder="Why this change was made"
                        />
                      </div>
                    </div>

                    <div className="mt-3 d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={saving}
                        onClick={handleSaveSubscription}
                      >
                        {saving ? 'Saving…' : 'Save subscription changes'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {tab === 'invoices' && (
              <div>
                {(detail?.invoices || []).length === 0 ? (
                  <p className="text-muted">No subscription invoices for this pump yet.</p>
                ) : (
                  <>
                    <div className="table-responsive mb-3">
                      <table className="table table-sm table-hover">
                        <thead>
                          <tr>
                            <th>Invoice</th>
                            <th>Total</th>
                            <th>Pending</th>
                            <th>Status</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {detail.invoices.map((invoice) => (
                            <tr key={invoice.id} className={String(invoice.id) === String(selectedInvoiceId) ? 'table-active' : ''}>
                              <td>#{invoice.id}{invoice.subscription_id ? ` · sub #${invoice.subscription_id}` : ''}</td>
                              <td>{formatInrAmount(invoice.total)}</td>
                              <td>{formatInrAmount(invoice.amount_pending)}</td>
                              <td>{invoice.workflow || (invoice.is_draft ? 'draft' : 'approved')}</td>
                              <td>
                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => selectInvoice(invoice)}>
                                  Edit
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {selectedInvoice && (
                      <>
                        <h6 className="mb-2">Invoice #{selectedInvoice.id}</h6>
                        <div className="row g-2 mb-3">
                          <div className="col-md-4">
                            <label className="form-label small">Discount (₹)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="form-control"
                              value={invoiceForm.discount}
                              onChange={(e) => setInvoiceForm({ ...invoiceForm, discount: e.target.value })}
                            />
                          </div>
                          <div className="col-md-8">
                            <label className="form-label small">Invoice note</label>
                            <input
                              type="text"
                              className="form-control"
                              value={invoiceForm.note}
                              onChange={(e) => setInvoiceForm({ ...invoiceForm, note: e.target.value })}
                            />
                          </div>
                          <div className="col-12">
                            <label className="form-label small">Audit reason</label>
                            <input
                              type="text"
                              className="form-control"
                              value={invoiceForm.reason}
                              onChange={(e) => setInvoiceForm({ ...invoiceForm, reason: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="d-flex flex-wrap gap-2 mb-4">
                          <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={handleSaveInvoice}>
                            Save invoice values
                          </button>
                          {selectedInvoice.is_draft && (
                            <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleApproveInvoice}>
                              Approve invoice
                            </button>
                          )}
                        </div>

                        <h6 className="mb-2">{editingReceiptId ? `Edit receipt #${editingReceiptId}` : 'Add / edit receipt'}</h6>
                        <div className="row g-2 mb-2">
                          <div className="col-md-3">
                            <label className="form-label small">Amount</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="form-control"
                              value={receiptForm.amount}
                              onChange={(e) => setReceiptForm({ ...receiptForm, amount: e.target.value })}
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label small">Mode</label>
                            <select
                              className="form-select"
                              value={receiptForm.payment_mode}
                              onChange={(e) => setReceiptForm({ ...receiptForm, payment_mode: e.target.value })}
                            >
                              {PAYMENT_MODES.map((mode) => (
                                <option key={mode.value} value={mode.value}>{mode.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-3">
                            <label className="form-label small">Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={receiptForm.receipt_date}
                              onChange={(e) => setReceiptForm({ ...receiptForm, receipt_date: e.target.value })}
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label small">Reference</label>
                            <input
                              type="text"
                              className="form-control"
                              value={receiptForm.reference_no}
                              onChange={(e) => setReceiptForm({ ...receiptForm, reference_no: e.target.value })}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small">Paid by</label>
                            <input
                              type="text"
                              className="form-control"
                              value={receiptForm.paid_by}
                              onChange={(e) => setReceiptForm({ ...receiptForm, paid_by: e.target.value })}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label small">Note</label>
                            <input
                              type="text"
                              className="form-control"
                              value={receiptForm.note}
                              onChange={(e) => setReceiptForm({ ...receiptForm, note: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="d-flex gap-2 mb-3">
                          <button type="button" className="btn btn-success btn-sm" disabled={saving} onClick={handleSaveReceipt}>
                            {editingReceiptId ? 'Update receipt' : 'Add receipt'}
                          </button>
                          {editingReceiptId && (
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => {
                                setEditingReceiptId(null);
                                setReceiptForm((prev) => ({ ...prev, amount: '', reference_no: '', note: '' }));
                              }}
                            >
                              Cancel edit
                            </button>
                          )}
                        </div>

                        <div className="table-responsive">
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Mode</th>
                                <th>Amount</th>
                                <th>Ref</th>
                                <th />
                              </tr>
                            </thead>
                            <tbody>
                              {receipts.length === 0 ? (
                                <tr><td colSpan={5} className="text-muted">No receipts yet</td></tr>
                              ) : receipts.map((receipt) => (
                                <tr key={receipt.id}>
                                  <td>{receipt.receipt_date}</td>
                                  <td>{receipt.payment_mode}</td>
                                  <td>{formatInrAmount(receipt.amount)}</td>
                                  <td>{receipt.reference_no || '—'}</td>
                                  <td className="text-nowrap">
                                    <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => startEditReceipt(receipt)}>
                                      Edit
                                    </button>
                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteReceipt(receipt.id)}>
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {tab === 'audit' && (
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Action</th>
                      <th>By</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail?.audit || []).length === 0 ? (
                      <tr><td colSpan={4} className="text-muted">No subscription/invoice audit events yet</td></tr>
                    ) : detail.audit.map((row) => (
                      <tr key={row.id}>
                        <td className="small text-nowrap">{row.created_at}</td>
                        <td>
                          <div className="small fw-semibold">{row.action}</div>
                          <div className="small text-muted">{row.description}</div>
                        </td>
                        <td className="small">{row.admin_name}</td>
                        <td>
                          <pre className="admin-sub-audit-pre mb-0">
                            {row.payload ? JSON.stringify(row.payload, null, 2) : '—'}
                          </pre>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default AdminSubscriptionControlModal;
